import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TripStatus } from '../enums/trip-status.enum';
import { environment } from '../../environment';
import { Trip, TripPayload } from '../models/trip.model';
import { DuplicateTripPayload } from '../models/recurring-trip.model';
import { normalizeApiArray } from '../utils/api-response.util';

export interface TripFilters {
  startDate?: string;
  endDate?: string;
  routeId?: number;
  vehicleId?: number;
  driverId?: number;
  status?: TripStatus;
  organisationId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class TripService {
  private apiUrl = `${environment.apiUrl}/Trip`;

  constructor(private http: HttpClient) { }

  // Get all trips with optional filters
  getTrips(filters?: TripFilters): Observable<Trip[]> {
    let params = new HttpParams();
    
    if (filters) {
      Object.keys(filters).forEach(key => {
        const value = filters[key as keyof TripFilters];
        if (value !== undefined && value !== null) {
          params = params.set(key, value.toString());
        }
      });
    }
    
    return this.http.get<unknown>(this.apiUrl, { params }).pipe(
      map(response => normalizeApiArray<Trip>(response))
    );
  }

  // Get trip by ID
  getTripById(id: number): Observable<Trip> {
    return this.http.get<Trip>(`${this.apiUrl}/${id}`);
  }

  // Create new trip
  createTrip(tripData: TripPayload): Observable<Trip> {
    return this.http.post<Trip>(this.apiUrl, tripData);
  }

  // Update trip
  updateTrip(id: number, tripData: TripPayload): Observable<Trip> {
    return this.http.put<Trip>(`${this.apiUrl}/${id}`, tripData);
  }

  duplicateTrip(id: number, payload: DuplicateTripPayload): Observable<Trip> {
    return this.http.post<Trip>(`${this.apiUrl}/${id}/duplicate`, payload);
  }

  // Update trip status
  updateTripStatus(id: number, status: TripStatus): Observable<Trip> {
    return this.http.patch<Trip>(`${this.apiUrl}/${id}/status`, { status });
  }

  // Delete trip
  deleteTrip(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  // Get trips for a specific vehicle
  getVehicleTrips(vehicleId: number): Observable<Trip[]> {
    return this.http.get<Trip[]>(`${this.apiUrl}/vehicle/${vehicleId}`);
  }

  // Get trips for a specific driver
  getDriverTrips(driverId: number): Observable<Trip[]> {
    return this.http.get<Trip[]>(`${this.apiUrl}/driver/${driverId}`);
  }

  // Get trips for a specific route
  getRouteTrips(routeId: number): Observable<Trip[]> {
    return this.http.get<Trip[]>(`${this.apiUrl}/route/${routeId}`);
  }

  // Get trips for today
  getTodayTrips(): Observable<Trip[]> {
    return this.http.get<Trip[]>(`${this.apiUrl}/today`);
  }

  // Get upcoming trips
  getUpcomingTrips(days: number = 7): Observable<Trip[]> {
    return this.http.get<Trip[]>(`${this.apiUrl}/upcoming/${days}`);
  }

  // Get trip statistics
  getTripStats(): Observable<{
    totalTrips: number;
    scheduledTrips: number;
    ongoingTrips: number;
    completedTrips: number;
    cancelledTrips: number;
  }> {
    return this.http.get<any>(`${this.apiUrl}/stats`);
  }

  // Check vehicle availability for a time slot
  checkVehicleAvailability(vehicleId: number, startTime: string, endTime: string): Observable<{ available: boolean; conflictingTrip?: Trip }> {
    return this.http.post<{ available: boolean; conflictingTrip?: Trip }>(
      `${this.apiUrl}/availability/vehicle`,
      { vehicleId, startTime, endTime }
    );
  }

  // Check driver availability for a time slot
  checkDriverAvailability(driverId: number, startTime: string, endTime: string): Observable<{ available: boolean; conflictingTrip?: Trip }> {
    return this.http.post<{ available: boolean; conflictingTrip?: Trip }>(
      `${this.apiUrl}/availability/driver`,
      { driverId, startTime, endTime }
    );
  }

  // Start trip (set actual departure time)
  startTrip(tripId: number): Observable<Trip> {
    return this.http.post<Trip>(`${this.apiUrl}/${tripId}/start`, {});
  }

  // End trip (set actual arrival time)
  endTrip(tripId: number): Observable<Trip> {
    return this.http.post<Trip>(`${this.apiUrl}/${tripId}/end`, {});
  }
}
